/** API client: cookie session, stable error codes, no localStorage tokens. */

export interface ApiErrorBody {
  error: { code: string; message: string; request_id?: string };
}

export async function api<T = unknown>(
  path: string,
  init: RequestInit = {},
  retries = 2,
): Promise<T> {
  const headers = new Headers(init.headers);
  if (!headers.has("Accept")) headers.set("Accept", "application/json");
  if (init.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  headers.set("Origin", window.location.origin);
  let res: Response;
  try {
    res = await fetch(path, { ...init, headers, credentials: "same-origin" });
  } catch (e) {
    if (retries > 0) {
      await new Promise((r) => setTimeout(r, 150));
      return api<T>(path, init, retries - 1);
    }
    throw e;
  }

  // Handle transient query concurrency limit (HTTP 429 capacity_exhausted)
  if (res.status === 429 && retries > 0) {
    const delay = 120 + Math.random() * 150;
    await new Promise((r) => setTimeout(r, delay));
    return api<T>(path, init, retries - 1);
  }

  const text = await res.text();
  const body = text ? JSON.parse(text) : {};
  if (!res.ok) {
    const err = (body as ApiErrorBody).error ?? {
      code: "internal_error",
      message: `HTTP ${res.status}`,
    };
    throw Object.assign(new Error(err.message), { code: err.code, status: res.status });
  }
  return body as T;
}

export const get = <T>(p: string) => api<T>(p);
export const post = <T>(p: string, body?: unknown) =>
  api<T>(p, { method: "POST", body: body === undefined ? undefined : JSON.stringify(body) });
export const del = <T>(p: string) => api<T>(p, { method: "DELETE" });
